import { useContext, useState } from "react"
import LoadingOverlay from "@p-ban/common/src/components/overlay/LoadingOverlay/LoadingOverlay"
import lookup from "@p-ban/common/src/constants/lookup"
import type { ValueOf } from "@p-ban/common/src/types"
import USER_TYPE from "@p-ban/core/src/user/constant/USER_TYPE"
import useMutationApi from "@p-ban/common/src/core/api/useMutationApi"
import { userValidator } from "@p-ban/common/src/logic/validation/userValidator"
import { validator } from "@p-ban/common/src/logic/validation/validationScheme"
import { toHankakuNumber, toKatakana } from "@p-ban/common/src/utils/StringUtils"

import { Button } from "../../../shadcn/Button"
import { Input } from "../../../shadcn/Input"
import { Label } from "../../../shadcn/Label"
import { Checkbox } from "../../../shadcn/Checkbox"
import { RadioGroup, RadioGroupItem } from "../../../shadcn/RadioGroup"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/Select"
import { SignUpFormContext } from "../context/SignUpFormContext"
import useSignUpCodeValueQueries from "../hooks/useSignUpCodeValueQueries"
import useForm from "../../../hooks/useForm"
import signUpApi from "../api/signUpApi"
import useViaReferralLink from "../hooks/useViaReferralLink"
import * as GoogleAnalytics from "../../../lib/GoogleAnalytics"
import boosterCouponApi from "../../../api/coupon/boosterCouponApi"
import LINKS from "../constant/links"
import { EstimateRestoreConfirmModal } from "../components/EstimateRestoreConfirmModal"
import { Stepper } from "../components/Stepper"

const SignUpEntry = () => {
  const { values, setValues, setWasSignedUp } = useContext(SignUpFormContext)
  const isCorporate = values.personalOrCorporate?.isCorporate ?? false
  const { viaReferralLink } = useViaReferralLink()
  const options = useSignUpCodeValueQueries()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  // onSuccessの後始末（計測タグ送信待ち〜マイページ遷移）が完了するまでの処理中状態。
  // APIリクエスト中(isLoading)と合わせて、登録処理全体のローディング表示・二重送信防止に使用する。
  const [isProcessing, setIsProcessing] = useState(false)

  const { setFieldValue, getFieldProps, getFieldErrors, getFieldRef, preSubmit } = useForm(values, setValues, {
    correctors: {
      corporateNameKana: toKatakana,
      phoneNumber: toHankakuNumber,
    },
    validators: {
      personalOrCorporate: validator.required,
      password: userValidator.password,
      corporateName: isCorporate ? userValidator.corporateName : undefined,
      corporateNameKana: isCorporate ? userValidator.corporateNameKana : undefined,
      userFamilyName: userValidator.familyName,
      userFirstName: userValidator.firstName,
      phoneNumber: userValidator.phoneNumber,
      userAdvertisingMediaCode: viaReferralLink ? undefined : validator.required,
      couponCode: (code) => {
        return code === ""
          ? undefined
          : boosterCouponApi.valid(code).then((result) => {
              return result.isSuccess ? undefined : ["無効なクーポンコードです。"]
            })
      },
    },
  })

  const { isLoading, mutate, allErrorMessages } = useMutationApi(signUpApi.create, {
    onSuccess(result) {
      setWasSignedUp(true)
      setIsProcessing(true)
      GoogleAnalytics.sendCustomEvent("sign_up_complete", result.userId)

      if (result.redirectUrl) {
        const url = result.redirectUrl.startsWith("/kiban/") ? result.redirectUrl : `/kiban${result.redirectUrl}`
        setRedirectUrl(url)
        setTimeout(() => {
          setShowSuccessModal(true)
          setIsProcessing(false)
        }, 3000)
      } else {
        // 計測タグのリクエスト送信完了を待ってから遷移する。即時遷移すると、画像ビーコン方式の
        // Yahoo/Microsoft広告では送信途中のリクエストがキャンセルされ、集計が取りこぼされるため。
        setTimeout(() => {
          window.location.assign(LINKS.MY_PAGE_TOP)
        }, 3000)
      }
    },
  })

  const handleUserTypeChange = (value: string) => {
    setValues((prev) => ({
      ...prev,
      personalOrCorporate: lookup(USER_TYPE, value as ValueOf<typeof USER_TYPE>["value"]),
    }))
  }

  const handleServiceToUseChange = (serviceValue: string, checked: boolean) => {
    const nextValues = checked
      ? [...values.serviceToUse, serviceValue]
      : values.serviceToUse.filter((v) => v !== serviceValue)
    setFieldValue("serviceToUse", nextValues)
  }

  const handleSubmit = async () => {
    const { canSubmit } = await preSubmit()
    if (canSubmit) {
      mutate({ ...values, viaReferralLink })
    }
  }

  const handleClickEstimate = () => {
    if (redirectUrl) {
      window.location.assign(redirectUrl)
    }
  }

  return (
    <div className="flex min-h-screen items-start bg-white py-12">
      <div className="flex w-full flex-col gap-16">
        <Stepper currentStep={3} />
        <div className="flex items-center">
          <h1 className="text-[34px] font-bold leading-[1.235] tracking-[0.25px] text-[#32383e]">会員情報登録</h1>
        </div>
        <div className="flex h-12 items-center">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-[#32383e]">メールアドレス（ログインID）</span>
          </div>
          <span className="text-base text-[#32383e]">{values.loginEmail}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-[#32383e]">ユーザー種別</span>
            <span className="text-[12.9px] leading-[1.55] text-[#c41c1c]">*</span>
          </div>
          <div className="flex flex-col gap-2">
            <RadioGroup
              ref={getFieldRef("personalOrCorporate")}
              value={values.personalOrCorporate?.value ?? ""}
              onValueChange={handleUserTypeChange}
              className="flex w-[550px] gap-4"
            >
              <div className="flex flex-col gap-2">
                <Label
                  className={`flex h-20 w-60 cursor-pointer items-center rounded-lg border p-2 ${
                    values.personalOrCorporate?.value === USER_TYPE.CORPORATE.value
                      ? "border-[#51bc51] bg-white"
                      : "border-[#cdd7e1] bg-white"
                  }`}
                >
                  <div className="flex w-full items-center gap-2 px-1.5">
                    <RadioGroupItem value={USER_TYPE.CORPORATE.value} />
                    <span
                      className={`text-base font-bold tracking-[0.15px] ${
                        values.personalOrCorporate?.value === USER_TYPE.CORPORATE.value
                          ? "text-[#32383e]"
                          : "text-[#9fa6ad]"
                      }`}
                    >
                      法人登録
                    </span>
                  </div>
                </Label>
                <p className="w-60 text-sm leading-[1.42] text-[#9fa6ad]">
                  法人口座でお取引の場合は、コチラを選択。学校や研究所に属される方も「法人登録」となります。
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Label
                  className={`flex h-20 w-60 cursor-pointer items-center rounded-lg border p-2 ${
                    values.personalOrCorporate?.value === USER_TYPE.PERSONAL.value
                      ? "border-[#51bc51] bg-white"
                      : "border-[#cdd7e1] bg-white"
                  }`}
                >
                  <div className="flex w-full items-center gap-2 px-1.5">
                    <RadioGroupItem value={USER_TYPE.PERSONAL.value} />
                    <span
                      className={`text-base font-bold tracking-[0.15px] ${
                        values.personalOrCorporate?.value === USER_TYPE.PERSONAL.value
                          ? "text-[#32383e]"
                          : "text-[#9fa6ad]"
                      }`}
                    >
                      個人登録
                    </span>
                  </div>
                </Label>
                <p className="w-60 text-sm leading-[1.42] text-[#9fa6ad]">
                  個人や個人事業主の方はコチラを選択。お支払い方法は、クレジットカードのみです。
                </p>
              </div>
            </RadioGroup>
            {(getFieldErrors("personalOrCorporate") ?? []).length > 0 && (
              <p className="text-sm text-red-600">{getFieldErrors("personalOrCorporate")?.[0]}</p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-[#32383e]">パスワード</span>
            <span className="text-[12.9px] leading-[1.55] text-[#c41c1c]">*</span>
          </div>
          <div className="flex flex-col gap-2">
            <Input
              type="password"
              placeholder="パスワードを入力してください"
              maxLength={256}
              {...getFieldProps("password")}
              className="w-[550px]"
            />
            <p className="text-sm leading-[1.42] text-[#9fa6ad]">
              半角英大文字・小文字・数字・記号をそれぞれ1文字以上含む8文字以上
            </p>
            {(getFieldErrors("password") ?? []).length > 0 && (
              <p className="text-sm text-red-600">{getFieldErrors("password")?.[0]}</p>
            )}
          </div>
        </div>
        {isCorporate && (
          <div className="flex items-center justify-between">
            <div className="flex w-1/2 items-center gap-2">
              <span className="text-base font-semibold text-[#32383e]">会社名・学校名</span>
              <span className="text-[12.9px] leading-[1.55] text-[#c41c1c]">*</span>
            </div>
            <div className="flex w-full flex-col gap-2">
              <Input
                placeholder="会社名または学校名を入力してください"
                {...getFieldProps("corporateName")}
                className="w-[550px]"
              />
              <p className="text-sm leading-[1.42] text-[#9fa6ad]">25文字以内、半角カナ不可</p>
              {(getFieldErrors("corporateName") ?? []).length > 0 && (
                <p className="text-sm text-red-600">{getFieldErrors("corporateName")?.[0]}</p>
              )}
            </div>
          </div>
        )}
        {isCorporate && (
          <div className="flex items-center justify-between">
            <div className="flex w-1/5 items-center gap-2">
              <span className="text-base font-semibold text-[#32383e]">
                会社名・学校名
                <br />
                （フリガナ）
              </span>
              <span className="text-[12.9px] leading-[1.55] text-[#c41c1c]">*</span>
            </div>
            <div className="flex w-[550px] flex-col gap-2">
              <Input
                placeholder="会社名または学校名のフリガナを入力してください"
                {...getFieldProps("corporateNameKana")}
              />
              <p className="text-sm leading-[1.42] text-[#9fa6ad]">40文字以内、半角カナ不可</p>
              {(getFieldErrors("corporateNameKana") ?? []).length > 0 && (
                <p className="text-sm text-red-600">{getFieldErrors("corporateNameKana")?.[0]}</p>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex w-[180px] items-center gap-2">
            <span className="text-base font-semibold text-[#32383e]">氏名</span>
            <span className="text-[12.9px] leading-[1.55] text-[#c41c1c]">*</span>
          </div>
          <div className="flex w-[550px] flex-col gap-2">
            <div className="flex justify-between">
              <div className="flex items-center">
                <Label className="text-base font-semibold text-[#32383e]">姓</Label>
                <Input {...getFieldProps("userFamilyName")} className="ml-2" />
              </div>
              <div className="flex items-center">
                <Label className="text-base font-semibold text-[#32383e]">名</Label>
                <Input {...getFieldProps("userFirstName")} className="ml-2" />
              </div>
            </div>
            <p className="text-sm leading-[1.42] text-[#9fa6ad]">8文字以内、半角カナ不可</p>
            {((getFieldErrors("userFamilyName") ?? []).length > 0 ||
              (getFieldErrors("userFirstName") ?? []).length > 0) && (
              <p className="text-sm text-red-600">
                {getFieldErrors("userFamilyName")?.[0] || getFieldErrors("userFirstName")?.[0]}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex w-[180px] items-center gap-2">
            <span className="text-base font-semibold text-[#32383e]">電話番号</span>
            <span className="text-[12.9px] leading-[1.55] text-[#c41c1c]">*</span>
          </div>
          <div className="flex w-[550px] flex-col gap-2">
            <Input
              type="tel"
              placeholder="会社名または学校名の電話番号を入力してください"
              {...getFieldProps("phoneNumber")}
            />
            {(getFieldErrors("phoneNumber") ?? []).length > 0 && (
              <p className="text-sm text-red-600">{getFieldErrors("phoneNumber")?.[0]}</p>
            )}
          </div>
        </div>
        {!viaReferralLink && (
          <div className="flex items-start justify-start gap-2">
            <div className="flex w-[180px] items-center">
              <div className="text-base font-semibold text-[#32383e]">
                <p className="mb-0">本サービスを</p>
                <p>知ったきっかけ</p>
              </div>
              <span className="text-[12.9px] leading-[1.55] text-[#c41c1c]">*</span>
            </div>
            <div className="flex w-[550px] flex-col gap-2">
              <Select
                value={values.userAdvertisingMediaCode ?? ""}
                onValueChange={(value) => setFieldValue("userAdvertisingMediaCode", value)}
              >
                <SelectTrigger ref={getFieldRef("userAdvertisingMediaCode")} className="w-[250px]">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {options.userAdvertisingMediaCode.map(({ label, value }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(getFieldErrors("userAdvertisingMediaCode") ?? []).length > 0 && (
                <p className="text-sm text-red-600">{getFieldErrors("userAdvertisingMediaCode")?.[0]}</p>
              )}
            </div>
          </div>
        )}
        <div className="flex items-start">
          <div className="flex w-[180px] justify-start gap-2">
            <span className="text-base font-semibold text-[#32383e]">
              利用したいサービス
              <br />
              （複数回答可）
            </span>
          </div>
          <div className="grid w-[550px] grid-cols-2 gap-y-2">
            {options.serviceToUse.map(({ label, value }) => (
              <div key={value} className="flex items-center gap-3">
                <Checkbox
                  checked={values.serviceToUse.includes(value)}
                  onCheckedChange={(checked) => handleServiceToUseChange(value, checked === true)}
                />
                <Label className="text-base font-medium leading-6 text-[#32383e]">{label}</Label>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-start">
          <div className="flex w-[180px] gap-2">
            <span className="text-base font-semibold text-[#32383e]">
              クーポンコード
              <br />
              （お友達紹介）
            </span>
          </div>
          <div className="flex w-[550px] flex-col gap-1">
            <Input
              placeholder="クーポンコードを入力してください"
              {...getFieldProps("couponCode")}
              readOnly={viaReferralLink}
            />
            <p className="text-sm leading-[1.42] text-[#9fa6ad]">(半角英数字)</p>
            <a href={LINKS.REFER_FRIENDS_CAMPAIGN} target="_blank" rel="noreferrer" className="text-sm text-[#16a34a]">
              お友達紹介とは
            </a>
            {(getFieldErrors("couponCode") ?? []).length > 0 && (
              <p className="text-sm text-red-600">{getFieldErrors("couponCode")?.[0]}</p>
            )}
          </div>
        </div>
        {(allErrorMessages ?? []).length > 0 && (
          <div className="text-sm text-red-600">
            {(allErrorMessages ?? []).map((msg, i) => (
              <p key={i}>{msg}</p>
            ))}
          </div>
        )}
        <div className="flex flex-col items-center gap-2">
          <Button
            onClick={handleSubmit}
            disabled={isLoading || isProcessing}
            className="h-16 w-[360px] rounded-[32px] bg-[#16a34a] text-base font-semibold text-white hover:bg-[#15803d]"
          >
            登録する
          </Button>
        </div>
      </div>

      {(isLoading || isProcessing) && <LoadingOverlay fullScreen />}

      <EstimateRestoreConfirmModal isOpen={showSuccessModal} onClickEstimate={handleClickEstimate} />
    </div>
  )
}

export default SignUpEntry
